/**
 * New Instruction Modal
 * ISOLATED MODULE - Uses /api/sales-instructions/* endpoints only
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Search, Home, Building2 } from "lucide-react";
import { API } from "@/App";
import { PostcodeLookup } from "@/components/PostcodeLookup";

const TENURE_OPTIONS = [
  { value: "FREEHOLD", label: "Freehold" },
  { value: "LEASEHOLD", label: "Leasehold" },
  { value: "SHARE_OF_FREEHOLD", label: "Share of Freehold" },
  { value: "COMMONHOLD", label: "Commonhold" },
  { value: "UNKNOWN", label: "Unknown" }
];

const PRICE_TYPE_OPTIONS = [
  { value: "GUIDE_PRICE", label: "Guide Price" },
  { value: "OFFERS_OVER", label: "Offers Over" },
  { value: "OFFERS_IN_REGION", label: "Offers in Region" },
  { value: "FIXED_PRICE", label: "Fixed Price" },
  { value: "AUCTION", label: "Auction Guide" }
];

const PROPERTY_TYPES = [
  "Detached",
  "Semi Detached", 
  "Terraced",
  "End Terrace",
  "Flat/Apartment",
  "Maisonette",
  "Bungalow",
  "Cottage",
  "Town House",
  "Land",
  "Other"
];

const SINewInstructionModal = ({ open, onClose, onCreated }) => {
  const [activeTab, setActiveTab] = useState("address");
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("token");

  const [formData, setFormData] = useState({
    // Address
    address_line_1: "",
    address_line_2: "",
    town: "",
    county: "",
    postcode: "",
    // Property
    property_type: "",
    bedrooms: "",
    bathrooms: "",
    tenure: "FREEHOLD",
    size: "",
    epc_rating: "",
    // Sales Terms
    price_amount: "",
    price_type: "GUIDE_PRICE",
    // Seller
    seller_name: "",
    seller_email: "",
    seller_phone: ""
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.address_line_1 || !formData.town || !formData.postcode) {
      toast.error("Please fill in required address fields");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
        price_amount: formData.price_amount ? parseInt(formData.price_amount.replace(/,/g, "")) : null
      };

      const response = await axios.post(
        `${API}/sales-instructions/instructions`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        onCreated(response.data.instruction_id);
      }
    } catch (error) {
      console.error("Error creating instruction:", error);
      toast.error(error.response?.data?.detail || "Failed to create instruction");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      address_line_1: "",
      address_line_2: "",
      town: "",
      county: "",
      postcode: "",
      property_type: "",
      bedrooms: "",
      bathrooms: "",
      tenure: "FREEHOLD",
      size: "",
      epc_rating: "",
      price_amount: "",
      price_type: "GUIDE_PRICE",
      seller_name: "",
      seller_email: "",
      seller_phone: ""
    });
    setActiveTab("address");
    setPostcodeLookup("");
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) {
        resetForm();
        onClose();
      }
    }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Home className="w-5 h-5 text-blue-600" />
            Add New Property Instruction
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="address">Address</TabsTrigger>
            <TabsTrigger value="property">Property</TabsTrigger>
            <TabsTrigger value="sales">Sales Terms</TabsTrigger>
            <TabsTrigger value="seller">Seller</TabsTrigger>
          </TabsList>

          {/* Address Tab */}
          <TabsContent value="address" className="space-y-4 mt-4">
            {/* Proper Postcode Lookup with Address Selection */}
            <PostcodeLookup
              onAddressSelect={(addr) => {
                setFormData(prev => ({
                  ...prev,
                  address_line_1: addr.line_1 || "",
                  address_line_2: addr.line_2 || "",
                  town: addr.town || "",
                  county: addr.county || "",
                  postcode: addr.postcode || ""
                }));
                toast.success("Address selected");
              }}
            />

            <div className="border-t pt-4">
              <p className="text-sm text-slate-500 mb-3">Or enter address manually:</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address_line_1">Address Line 1 *</Label>
              <Input
                id="address_line_1"
                placeholder="e.g. 123 High Street"
                value={formData.address_line_1}
                onChange={(e) => handleChange("address_line_1", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address_line_2">Address Line 2</Label>
              <Input
                id="address_line_2"
                placeholder="e.g. Flat 2"
                value={formData.address_line_2}
                onChange={(e) => handleChange("address_line_2", e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="town">Town/City *</Label>
                <Input
                  id="town"
                  placeholder="e.g. Bedford"
                  value={formData.town}
                  onChange={(e) => handleChange("town", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="county">County</Label>
                <Input
                  id="county"
                  placeholder="e.g. Bedfordshire"
                  value={formData.county}
                  onChange={(e) => handleChange("county", e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="postcode">Postcode *</Label>
              <Input
                id="postcode"
                placeholder="e.g. MK42 7AB"
                value={formData.postcode}
                onChange={(e) => handleChange("postcode", e.target.value.toUpperCase())}
                className="w-[200px]"
              />
            </div>
          </TabsContent>

          {/* Property Tab */}
          <TabsContent value="property" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Property Type</Label>
              <Select
                value={formData.property_type}
                onValueChange={(v) => handleChange("property_type", v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select property type" />
                </SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bedrooms">Bedrooms</Label>
                <Input
                  id="bedrooms"
                  type="number"
                  min="0"
                  placeholder="e.g. 3"
                  value={formData.bedrooms}
                  onChange={(e) => handleChange("bedrooms", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bathrooms">Bathrooms</Label>
                <Input
                  id="bathrooms"
                  type="number"
                  min="0"
                  placeholder="e.g. 2"
                  value={formData.bathrooms}
                  onChange={(e) => handleChange("bathrooms", e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tenure</Label>
              <Select
                value={formData.tenure}
                onValueChange={(v) => handleChange("tenure", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TENURE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="size">Size</Label>
                <Input
                  id="size"
                  placeholder="e.g. 1,250 sq ft"
                  value={formData.size}
                  onChange={(e) => handleChange("size", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="epc_rating">EPC Rating</Label>
                <Input
                  id="epc_rating"
                  placeholder="e.g. C"
                  value={formData.epc_rating}
                  onChange={(e) => handleChange("epc_rating", e.target.value.toUpperCase())}
                  maxLength={1}
                  className="w-[100px]"
                />
              </div>
            </div>
          </TabsContent>

          {/* Sales Terms Tab */}
          <TabsContent value="sales" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="price_amount">Asking Price</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
                <Input
                  id="price_amount"
                  placeholder="395,000"
                  value={formData.price_amount}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9,]/g, "");
                    handleChange("price_amount", value);
                  }}
                  className="pl-8"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Price Type</Label>
              <Select
                value={formData.price_type}
                onValueChange={(v) => handleChange("price_type", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </TabsContent>

          {/* Seller Tab */}
          <TabsContent value="seller" className="space-y-4 mt-4">
            <div className="p-4 bg-slate-50 rounded-lg mb-4">
              <p className="text-sm text-slate-600">
                Add seller details to enable contract sending and portal access.
                These can be updated later.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="seller_name">Seller Name</Label>
              <Input
                id="seller_name"
                placeholder="e.g. Mr & Mrs Smith"
                value={formData.seller_name}
                onChange={(e) => handleChange("seller_name", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="seller_email">Seller Email</Label>
              <Input
                id="seller_email"
                type="email"
                placeholder="e.g. seller@email.com"
                value={formData.seller_email}
                onChange={(e) => handleChange("seller_email", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="seller_phone">Seller Phone</Label>
              <Input
                id="seller_phone"
                type="tel"
                placeholder="e.g. 07123 456789"
                value={formData.seller_phone}
                onChange={(e) => handleChange("seller_phone", e.target.value)}
              />
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-between pt-4 border-t mt-6">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <div className="flex gap-2">
            {activeTab !== "address" && (
              <Button
                variant="outline"
                onClick={() => {
                  const tabs = ["address", "property", "sales", "seller"];
                  const idx = tabs.indexOf(activeTab);
                  if (idx > 0) setActiveTab(tabs[idx - 1]);
                }}
              >
                Previous
              </Button>
            )}
            {activeTab !== "seller" ? (
              <Button
                onClick={() => {
                  const tabs = ["address", "property", "sales", "seller"];
                  const idx = tabs.indexOf(activeTab);
                  if (idx < tabs.length - 1) setActiveTab(tabs[idx + 1]);
                }}
              >
                Next
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Instruction"
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SINewInstructionModal;
